import React, { useEffect, useState } from "react";
import { InvoiceFilterModel } from "../../core/api/invoice/InvoiceFilterModel";
import { StatisticsModel, UserInvoiceStatistics } from "../../core/api/statistics/StatisticsModel";
import { 
  DataGrid, 
  GridColDef, 
  GridRenderCellParams 
} from "@mui/x-data-grid";
import { Box, Typography } from "@mui/material";

export interface TableProps {
  filter?: InvoiceFilterModel;
}

function calculateTotal(data: UserInvoiceStatistics[]): UserInvoiceStatistics {
  return {
    createdByName: "Total",
    pvm: data.reduce((sum, item) => sum + (item.pvm ?? 0), 0),
    sum: data.reduce((sum, item) => sum + (item.sum ?? 0), 0),
    count: data.reduce((sum, item) => sum + item.count, 0),
    discount: data.reduce((sum, item) => sum + (item.discount ?? 0), 0),
    price: data.reduce((sum, item) => sum + (item.price ?? 0), 0),
  };
}

const InvoiceStatisticsTable: React.FC<TableProps> = (props) => {
  const [data, setData] = useState<UserInvoiceStatistics[]>([]);
  const [total, setTotal] = useState<UserInvoiceStatistics | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const statistics = await StatisticsModel.getUserInvoiceStatistics(props.filter);
      const totalStats = calculateTotal(statistics);
      setData(statistics);
      setTotal(totalStats);
    };

    fetchData().catch(console.error);
  }, [props.filter]);

  // Prepare rows with IDs for DataGrid
  const rows = total 
    ? [...data.map((item, index) => ({ ...item, id: index })), { ...total, id: 'total' }]
    : data.map((item, index) => ({ ...item, id: index }));

  const columns: GridColDef[] = [
    { 
      field: "createdByName",
      headerName: "Vartotojas",
      flex: 1,
      renderCell: (params: GridRenderCellParams<UserInvoiceStatistics>) => 
        params.value ?? "-"
    },
    {
      field: "count",
      headerName: "Sąskaitų skaičius",
      flex: 1,
      type: "number",
    },
    {
      field: "price",
      headerName: "Kaina",
      flex: 1,
      type: "number"
    },
    {
      field: "discount",
      headerName: "Nuolaida",
      flex: 1,
      type: "number"
    },
    {
      field: "pvm",
      headerName: "PVM",
      flex: 1,
      type: "number"    },
    {
      field: "sum",
      headerName: "Viso",
      flex: 1,
      type: "number"    },
  ];

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      bgcolor: 'background.paper',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f5f5f5', 
          fontWeight: 'bold',
          fontSize: '1.1rem',
          borderBottom: '1px solid #ddd'
        }}
      >
        <Typography variant="h6">
          {`Statistika nuo ${props.filter?.dateFrom || ''} iki ${props.filter?.dateTo || ''}`}
        </Typography>
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        hideFooter
        getRowClassName={(params) => 
          params.id === 'total' ? 'total-row' : params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
        }
        sx={{
          border: 'none',
          '& .total-row': {
            backgroundColor: '#e0e0e0',
            fontWeight: 'bold',
          },
          '& .even-row': {
            backgroundColor: '#ffffff',
          },
          '& .odd-row': {
            backgroundColor: '#efefef',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#bdbdbd',
          },
          '& .MuiDataGrid-virtualScroller': {
            minHeight: '200px',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-row': {
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              Sąskaitų statistikos nėra
            </Box>
          ),
        }}
      />
    </Box>
  );
};

export default InvoiceStatisticsTable;
